'use strict';

import axios from 'axios';
import { startSpinner, stopSpinner } from '@jscomponents/spinner';
import {
  listenDOMEvent,
  createElement,
  addDynamicEventListener
} from '@jspartials/dom-api-utils';
import { gtmListenProductTiles, gtmApplyFilter } from '@jspartials/gtm-utils';
import {
  listenButtonShowMore,
  restorePreviousPages
} from '@jspages/search/loadMoreProducts';
import { listenSortingButtons } from '@jspages/search/sortingButtons';
import { updateCollaborationSelect } from '@jspages/search/collaborationSelect';
import { updateUrlLink, getAjaxUrlWithParams } from '@jspartials/filter-utils';
import { readPlpRestoreState } from '@jspartials/plp-target-product';
import { customEvent } from '@jspartials/general-utils';
import Recommendations from '@jsclasses/recommendations';
import { classes, addClassToElement } from '@jspartials/class-list-utils';
import { listenImagesCarousel } from '@jspages/search/imagesCarouselTiles';
import { listenVideoProductsCarousel } from '@jspages/search/videoProductsCarousel';

/**
 * El navegador puede restaurar el scroll de la entrada anterior del history
 * (scrollRestoration 'auto', el valor por defecto) antes de que corra este
 * script, incluso antes de DOMContentLoaded. Si venimos de la PDP con páginas
 * que restaurar, ese Y guardado corresponde a una página mucho más alta (con
 * "ver más" ya pulsado varias veces) que la que el servidor acaba de
 * renderizar (solo la primera página): el navegador lo recorta al fondo,
 * salto visible, y luego nuestro propio scroll lo corrige. Se desactiva la
 * restauración nativa solo cuando sabemos que vamos a hacer scroll nosotros
 * mismos (hay un productId guardado); en cualquier otra vuelta con el
 * navegador se deja el comportamiento nativo tal cual.
 *
 * Leído aquí, a nivel de módulo, lo antes posible: readPlpRestoreState()
 * consume el sessionStorage, así que solo puede llamarse una vez.
 */
const plpRestoreState = readPlpRestoreState();

if (plpRestoreState.productId && 'scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// Selectors
const selector = {
  refinementBar: '.js-refine-bar',
  refinements: '.js-refinements',
  sortingButton: '.js-sorting-button',
  applyFilters: '.js-apply-filters',
  resetFilters: '.js-reset-filters',
  toggleFilters: '.js-toggle-filters',
  recommendations: '.js-search-results__recommendations',
  productTileList: '.js-grid-products',
  productTile: '.js-plp-list__item',
  productTileRoot: '.js-product-tile',
  countdown: '.plp .js-countdown',
  seoContentWrapper: '.js-seo-content-wrapper'
};

// Variables
const eventReloadVtoButtons = new CustomEvent(customEvent.reloadVtoButtons);
const eventListenProductTiles = new CustomEvent(customEvent.listenProductTiles);
const eventInitProductTilesAnimation = new CustomEvent(
  customEvent.initProductTilesAnimation
);
const eventInitLazyVideos = new CustomEvent(customEvent.initLazyVideos);

// Functions

/**
 * Update DOM elements with Ajax results
 * @param {HTMLElement} results - DOM element
 * @param {String} selector - DOM element selector to look up in the results
 * @return {void}
 */
const updateDom = (results, selector) => {
  const currentDomElement = document.querySelector(selector) || null;
  const updateDomElement = results.querySelector(selector) || null;

  if (currentDomElement && updateDomElement) {
    currentDomElement.innerHTML = updateDomElement.innerHTML;
  }
};

/**
 * Parse Ajax results and updated select DOM elements
 * @param {String} response - HTML code
 * @return {void}
 */
const parseResults = (response) => {
  const resultHtml = createElement({
    tag: 'div',
    content: response
  });
  const selectorsToUpdate = [
    selector.refinements,
    '.js-grid-header',
    '.js-applied-filters',
    selector.productTileList,
    '.js-shortcut-filters',
    '.js-fixed-filter',
    '.js-seo-content-top',
    '.js-seo-content-bottom',
    '.js-button-see-results',
    '.js-message-results-found',
    '.js-button-show-filters',
    '.js-total-results-found',
    selector.resetFilters
  ];

  selectorsToUpdate.forEach((selector) => {
    updateDom(resultHtml, selector);
  });
};

/**
 * Scroll to a given product tile
 *
 * El pid guardado es el que el servidor pintó en `data-pid`, incluso si el
 * usuario había seleccionado otra variante de color en el tile: eso lo resuelve
 * gtm-utils.js al guardar el estado, leyendo el `defaultPid` que conserva
 * product-tile.js. Aquí basta la búsqueda directa.
 * @param {string} productId - Product id to scroll into view
 * @returns {Promise<void>} - Resolves once the scroll has been triggered
 */
const scrollToProduct = (productId) =>
  new Promise((resolve) => {
    setTimeout(() => {
      // Ojo: los swatches de color de cada tile llevan también `data-pid` (el
      // de las variantes hermanas), así que buscar como descendiente de
      // cualquier tile encuentra antes un swatch en un tile anterior que el
      // propio tile buscado. `.js-product-tile` solo lo lleva el tile raíz.
      const target = productId
        ? document.querySelector(
            `${selector.productTileRoot}[data-pid="${productId}"]`
          )
        : null;

      if (target) {
        target.scrollIntoView({ block: 'center' });
      } else if (productId) {
        // Había un producto que buscar y no está en el grid. Sin esto la página
        // se queda donde la dejó la restauración nativa del navegador (que con
        // `defer` puede haberse aplicado antes de que la desactivemos): una
        // posición arbitraria a media lista. Arriba es predecible.
        window.scrollTo(0, 0);
      }

      resolve();
    }, 250);
  });

/**
 * Restore the PLP state (pages loaded and scroll position) after coming back
 * from the PDP
 *
 * Con páginas que restaurar, todo el proceso (recarga + scroll) queda tapado
 * por un único spinner de principio a fin: sin eso, el usuario ve el grid
 * crecer a saltos y encima un scrollIntoView instantáneo aterrizando sobre un
 * tile que aún se está animando. Sin páginas que restaurar (el caso normal)
 * el comportamiento es el de siempre, sin spinner.
 * @returns {void}
 */
const restorePlpStateOnInit = () => {
  const { productId, pagesToRestore } = plpRestoreState;

  if (pagesToRestore > 0) {
    startSpinner();

    restorePreviousPages(pagesToRestore)
      .then(() => scrollToProduct(productId))
      .then(() => {
        stopSpinner();
      });
  } else {
    scrollToProduct(productId);
  }
};

/**
 * Listen filters triggers clicks
 * @returns {void}
 */
const listenFiltersTriggers = () => {
  const filtersTriggers =
    document.querySelectorAll(selector.applyFilters) || [];
  const toggleFilters = document.querySelectorAll(selector.toggleFilters) || [];

  filtersTriggers.forEach((trigger) => {
    listenDOMEvent(trigger, 'click', () => {
      applyFilter(trigger);
    });
  });

  toggleFilters.forEach((trigger) => {
    listenDOMEvent(trigger, 'change', () => {
      applyFilter(trigger);
    });
  });
};

/**
 * Apply filter
 * @param {HTMLElement} button - Button to apply filter
 * @returns {void}
 */
const applyFilter = (button) => {
  const urlAjax = getAjaxUrlWithParams(button.dataset.params);
  const urlFinal = getUrlIncludingSortingRule(urlAjax);

  startSpinnerFilters(button);
  gtmApplyFilter(button);

  axios
    .get(urlFinal)
    .then((response) => {
      updateContent(response.data);
    })
    .catch((error) => {
      console.error(error);
    })
    .then(() => {
      stopSpinnerFilters(button);
    });
};

/**
 * Get url including sorting rule
 * @param {String} url - Url to get
 * @returns {String} Url including sorting rule
 */
const getUrlIncludingSortingRule = (url) => {
  const urlWithoutSortingRule = url.split('&srule=')[0];
  const currentSortingButton =
    document.querySelector(`${selector.sortingButton}`) || null;
  const currentSortingOption = currentSortingButton
    ? currentSortingButton.options[currentSortingButton.selectedIndex]
    : null;
  const sortingRuleId = currentSortingOption
    ? currentSortingOption?.dataset?.sortingRuleId
    : '';

  // Sin regla de ordenación se devuelve la URL tal cual, no una cadena vacía.
  // applyFilter no comprueba el resultado, así que el '' anterior acababa en un
  // axios.get('') que pedía la página actual y repintaba el DOM con el
  // contenido SIN filtrar: el filtro aparentaba no hacer nada. Solo se notaba
  // en sites cuyas categorías no tienen sorting rules asignadas.
  return sortingRuleId
    ? `${urlWithoutSortingRule}&srule=${sortingRuleId}`
    : urlWithoutSortingRule;
};

/**
 * Make an ajax request to search
 * @param {String} url Url to call
 * @returns {void}
 */
const ajaxRequestToSearch = (url) => {
  const urlFinal = getUrlIncludingSortingRule(url);

  if (urlFinal) {
    startSpinner();

    axios
      .get(urlFinal)
      .then((response) => {
        updateContent(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .then(() => {
        stopSpinner();
      });
  }
};

/**
 * Update content of the page after ajax request
 * @param {String} content - HTML code
 * @returns {void}
 */
const updateContent = (content) => {
  parseResults(content);
  listenImagesCarousel();
  listenVideoProductsCarousel();
  listenFiltersTriggers();
  listenSortingButtons();
  listenButtonShowMore();
  updateUrlLink();
  updateCollaborationSelect();
  gtmListenProductTiles(true);
  document.dispatchEvent(eventListenProductTiles);
  document.dispatchEvent(eventReloadVtoButtons);
  document.dispatchEvent(eventInitProductTilesAnimation);
  document.dispatchEvent(eventInitLazyVideos);
};

/**
 * Start spinner over filters
 * @param {HTMLElement} element - Element that triggers the spinner
 * @returns {void}
 */
const startSpinnerFilters = (element) => {
  const isInsideRefinements = element.closest(selector.refinementBar);

  if (isInsideRefinements) {
    startSpinner(selector.refinements);
  } else {
    startSpinner();
  }
};

/**
 * Stop spinner over filters
 * @param {HTMLElement} element - Element that triggers the spinner
 * @returns {void}
 */
const stopSpinnerFilters = (element) => {
  const isInsideRefinements = element.closest(selector.refinements);

  if (isInsideRefinements) {
    stopSpinner(selector.refinements);
  } else {
    stopSpinner();
  }
};

/**
 * Init recommendations
 * @returns {void}
 */
const initRecommendations = () => {
  const recommendations =
    document.querySelector(selector.recommendations) || null;

  if (recommendations) {
    new Recommendations({
      wrapper: recommendations,
      showPrice: true,
      maxVisibleItems: 5
    });
  }
};

/**
 * Reveal not-yet-animated product tiles
 * @param {boolean} [instant] - Skip the entrance transition (e.g. tiles being
 *   silently restored after coming back from the PDP, not newly discovered)
 * @returns {void}
 */
const initProductTilesAnimation = (instant) => {
  const productTiles = document.querySelectorAll(
    `${selector.productTileList} ${selector.productTile}:not(.${classes.is.animated})`
  );

  const productTileKeyframe = [
    { transform: 'translateY(60px)', opacity: '0' },
    { transform: 'translateY(0px)', opacity: '1' }
  ];

  productTiles.forEach((productTile) => {
    productTile.animate(productTileKeyframe, {
      duration: instant ? 0 : 600,
      delay: instant ? 0 : 100,
      easing: 'ease',
      fill: 'both'
    });

    addClassToElement(productTile, classes.is.animated);
  });
};

/**
 * Move countdown from the slot to the resultsNoDecorator title in the PLP
 * @returns {void}
 */

const moveCountdownNextToTitle = () => {
  const countdown = document.querySelector(selector.countdown);

  if (countdown) {
    const wrapper = document.querySelector(selector.seoContentWrapper);
    wrapper && wrapper.appendChild(countdown);
  }
};

/**
 * Site in-store (catálogo tablets): valor de una cookie leída del navegador
 * @param {String} name - Nombre de la cookie
 * @returns {String} Valor de la cookie, o cadena vacía si no existe
 */
const getCookieValue = (name) => {
  const match = document.cookie.match(`(^|;\\s*)${name}=([^;]*)`);

  return match ? decodeURIComponent(match[2]) : '';
};

/**
 * Site in-store (catálogo tablets): fuerza que la PLP lleve el refinamiento
 * hk_storesWithStock de la tienda activa (cookie 'storeId'), sin importar
 * desde dónde se navegó hasta aquí.
 *
 * POR QUÉ EXISTE
 * El servidor también intenta forzar este refinamiento con un redirect
 * (instoreCatalogFilter.ensureStoreRefinement), pero eso solo funciona si la
 * petición llega al app server. Si la URL sin refinamiento ya está cacheada
 * (CDN/page cache), el redirect nunca se ejecuta. Comprobándolo aquí, en el
 * cliente, tras cargar la PLP (venga de un reload, de un link de menú, de una
 * miga de pan o de lo que sea), la corrección no depende de la caché de esa
 * petición: si no coincide, se navega directo a la URL correcta.
 *
 * SOLO PARA EL SITE IN-STORE
 * Este bundle es del cartridge compartido (app_custom_hawkers_frontend), así
 * que también se ejecuta en el site normal. La cookie 'storeId' se comparte
 * entre sites del mismo dominio, por lo que sin este guard una visita previa
 * al site de tiendas forzaba el refinamiento en la PLP del site normal.
 *
 * @returns {boolean} true si se ha disparado una navegación (la página actual
 *   ya no importa, se está recargando con la URL corregida)
 */
const ensureStoreRefinementOnLoad = () => {
  if (!globalVariables.currentSiteIsInStore) {
    return false;
  }

  // Debe coincidir con productHelper.customAttributes.storesWithStock /
  // instoreCatalogFilter.attributeId (servidor) y cookieHelpers 'storeId'.
  const attributeId = 'hk_storesWithStock';
  const storeId = getCookieValue('storeId');
  const params = new URLSearchParams(window.location.search);
  const otherPrefs = [];
  let currentStoreValue = '';
  let index = 1;

  while (params.has(`prefn${index}`)) {
    const key = params.get(`prefn${index}`);

    if (key === attributeId) {
      currentStoreValue = params.get(`prefv${index}`) || '';
    } else if (params.has(`prefmin${index}`)) {
      otherPrefs.push({
        key,
        min: params.get(`prefmin${index}`),
        max: params.get(`prefmax${index}`)
      });
    } else {
      otherPrefs.push({ key, value: params.get(`prefv${index}`) });
    }

    index += 1;
  }

  if (currentStoreValue === storeId) {
    return false;
  }

  for (let i = 1; i < index; i += 1) {
    params.delete(`prefn${i}`);
    params.delete(`prefv${i}`);
    params.delete(`prefmin${i}`);
    params.delete(`prefmax${i}`);
  }

  if (storeId) {
    otherPrefs.push({ key: attributeId, value: storeId });
  }

  otherPrefs.forEach((pref, i) => {
    const n = i + 1;

    params.set(`prefn${n}`, pref.key);

    if (pref.min !== undefined) {
      params.set(`prefmin${n}`, pref.min);
      params.set(`prefmax${n}`, pref.max);
    } else {
      params.set(`prefv${n}`, pref.value);
    }
  });

  const newSearch = params.toString();
  const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;

  window.location.replace(newUrl);

  return true;
};

// Init
document.addEventListener('DOMContentLoaded', function () {
  // Site in-store: corregir el refinamiento de tienda antes de nada más. Si no
  // coincide, ya se está navegando de nuevo: no seguir inicializando la página
  // vieja.
  if (ensureStoreRefinementOnLoad()) {
    return;
  }

  // Restore PLP state (loaded pages + scroll) after coming back from the PDP
  restorePlpStateOnInit();

  // Update collaboration select
  updateCollaborationSelect();

  // Listen filters triggers clicks
  listenFiltersTriggers();

  // Listen sort menu
  listenSortingButtons();

  // Load more products
  listenButtonShowMore();

  // Init recommendations
  initRecommendations();

  // Init product tiles animation
  initProductTilesAnimation();

  // Move countdown next to title
  moveCountdownNextToTitle();

  // Listen images carousel
  listenImagesCarousel();

  // Listen video products carousel
  listenVideoProductsCarousel();

  // Listen reset filters button. Delegado en document: el de los shortcut
  // filters se repinta con cada respuesta Ajax (parseResults) y un listener
  // directo se perdía tras aplicar el primer filtro.
  addDynamicEventListener(
    document,
    'click',
    selector.resetFilters,
    function () {
      applyFilter(this);
      this.blur();
    }
  );

  // Listen custom event to make ajax request to search
  document.addEventListener('sendAjaxRequestToSearch', (event) => {
    const details = event.detail;
    const url = details?.url || null;

    if (url) {
      ajaxRequestToSearch(url);
    }
  });

  // Listen custom event to init product tiles animation
  document.addEventListener(customEvent.initProductTilesAnimation, (event) => {
    initProductTilesAnimation(event?.detail?.instant);
  });
});
